import React, { useEffect, useMemo, useRef } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { OrbitControls } from "@react-three/drei";

function ImageInput({ label, image, features, showFeatures, onChange }) {
  const inputRef = useRef(null);

  function handleFile(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const url = URL.createObjectURL(file);
    onChange({ file, url });
    event.target.value = "";
  }

  return (
    <div className="reconstruction-image-slot">
      <div className="reconstruction-image-header">
        <span>{label}</span>
        <button
          type="button"
          className="reconstruction-button"
          onClick={() => inputRef.current?.click()}
        >
          LOAD IMAGE
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFile}
      />

      <div className="reconstruction-image-placeholder">
        {image ? (
          <div
            style={{
              position: "relative",
              display: "inline-block",
              maxWidth: "100%",
              lineHeight: 0,
            }}
          >
            <img
              src={image.url}
              alt={label}
              className="reconstruction-input-image"
            />

            {showFeatures && features && (
              <svg
                viewBox={`0 0 ${features.width} ${features.height}`}
                preserveAspectRatio="none"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                }}
              >
                {features.keypoints.map((keypoint, index) => (
                  <circle
                    key={index}
                    cx={keypoint.x}
                    cy={keypoint.y}
                    r={Math.max(2, keypoint.size * 0.12)}
                    fill="none"
                    stroke="#5cfa76"
                    strokeWidth="1.5"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
              </svg>
            )}
          </div>
        ) : (
          <span>No image loaded</span>
        )}
      </div>
    </div>
  );
}

function CorrespondenceVisualization({
  imageA,
  imageB,
  featuresA,
  featuresB,
  correspondences,
  message,
}) {
  if (!imageA || !imageB || !featuresA || !featuresB) {
    return (
      <div className="reconstruction-stage-message">
        Load two images to compute correspondences.
      </div>
    );
  }

  const widthA = featuresA.width;
  const heightA = featuresA.height;
  const widthB = featuresB.width;
  const heightB = featuresB.height;
  const displayHeight = Math.max(heightA, heightB);
  const scaledWidthA = widthA * (displayHeight / heightA);
  const scaledWidthB = widthB * (displayHeight / heightB);
  const gap = displayHeight * 0.03;
  const totalWidth = scaledWidthA + gap + scaledWidthB;
  const visibleCorrespondences = correspondences.slice(0, 250);

  return (
    <div style={{ width: "100%", overflow: "hidden" }}>
      <svg
        viewBox={`0 0 ${totalWidth} ${displayHeight}`}
        style={{
          display: "block",
          width: "100%",
          height: "auto",
        }}
      >
        <image
          href={imageA.url}
          x="0"
          y="0"
          width={scaledWidthA}
          height={displayHeight}
          preserveAspectRatio="none"
        />
        <image
          href={imageB.url}
          x={scaledWidthA + gap}
          y="0"
          width={scaledWidthB}
          height={displayHeight}
          preserveAspectRatio="none"
        />

        {visibleCorrespondences.map((correspondence, index) => {
          const pointA = correspondence.pointA;
          const pointB = correspondence.pointB;
          const x1 = pointA.x * (scaledWidthA / widthA);
          const y1 = pointA.y * (displayHeight / heightA);
          const x2 = scaledWidthA + gap + pointB.x * (scaledWidthB / widthB);
          const y2 = pointB.y * (displayHeight / heightB);

          return (
            <g key={index}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#5cfa76"
                strokeWidth="1"
                strokeOpacity="0.65"
                vectorEffect="non-scaling-stroke"
              />
              <circle
                cx={x1}
                cy={y1}
                r="3"
                fill="#5cfa76"
                vectorEffect="non-scaling-stroke"
              />
              <circle
                cx={x2}
                cy={y2}
                r="3"
                fill="#5cfa76"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}
      </svg>

      <div className="reconstruction-stage-message">{message}</div>
    </div>
  );
}

// Epipolar line clipping
function clipLineToImage(a, b, c, width, height) {
  const intersections = [];

  function addPoint(x, y) {
    if (
      Number.isFinite(x) &&
      Number.isFinite(y) &&
      x >= 0 &&
      x <= width &&
      y >= 0 &&
      y <= height
    ) {
      const duplicate = intersections.some(
        (point) => Math.abs(point.x - x) < 1e-6 && Math.abs(point.y - y) < 1e-6,
      );

      if (!duplicate) {
        intersections.push({ x, y });
      }
    }
  }

  if (Math.abs(b) > 1e-12) {
    addPoint(0, -c / b);
    addPoint(width, -(a * width + c) / b);
  }

  if (Math.abs(a) > 1e-12) {
    addPoint(-c / a, 0);
    addPoint(-(b * height + c) / a, height);
  }

  if (intersections.length < 2) {
    return null;
  }

  return [intersections[0], intersections[1]];
}

function epipolarLineInB(F, pointA) {
  const x = pointA.x;
  const y = pointA.y;

  return {
    a: F.get(0, 0) * x + F.get(0, 1) * y + F.get(0, 2),
    b: F.get(1, 0) * x + F.get(1, 1) * y + F.get(1, 2),
    c: F.get(2, 0) * x + F.get(2, 1) * y + F.get(2, 2),
  };
}

function epipolarLineInA(F, pointB) {
  const x = pointB.x;
  const y = pointB.y;

  return {
    a: F.get(0, 0) * x + F.get(1, 0) * y + F.get(2, 0),
    b: F.get(0, 1) * x + F.get(1, 1) * y + F.get(2, 1),
    c: F.get(0, 2) * x + F.get(1, 2) * y + F.get(2, 2),
  };
}

function EpipolarVisualization({
  imageA,
  imageB,
  featuresA,
  featuresB,
  geometry,
}) {
  if (!imageA || !imageB || !featuresA || !featuresB || !geometry) {
    return (
      <div className="reconstruction-stage-message">
        Unable to estimate epipolar geometry.
      </div>
    );
  }

  const widthA = featuresA.width;
  const heightA = featuresA.height;
  const widthB = featuresB.width;
  const heightB = featuresB.height;
  const displayHeight = Math.max(heightA, heightB);
  const scaledWidthA = widthA * (displayHeight / heightA);
  const scaledWidthB = widthB * (displayHeight / heightB);
  const gap = displayHeight * 0.03;
  const offsetB = scaledWidthA + gap;
  const totalWidth = scaledWidthA + gap + scaledWidthB;

  const maximumLines = 10;
  const step = Math.max(1, Math.floor(geometry.inliers.length / maximumLines));
  const visibleInliers = geometry.inliers
    .filter((_, index) => index % step === 0)
    .slice(0, maximumLines);

  const scaleAX = scaledWidthA / widthA;
  const scaleAY = displayHeight / heightA;
  const scaleBX = scaledWidthB / widthB;
  const scaleBY = displayHeight / heightB;

  return (
    <div style={{ width: "100%", overflow: "hidden" }}>
      <svg
        viewBox={`0 0 ${totalWidth} ${displayHeight}`}
        style={{
          display: "block",
          width: "100%",
          height: "auto",
        }}
      >
        <image
          href={imageA.url}
          x="0"
          y="0"
          width={scaledWidthA}
          height={displayHeight}
          preserveAspectRatio="none"
        />
        <image
          href={imageB.url}
          x={offsetB}
          y="0"
          width={scaledWidthB}
          height={displayHeight}
          preserveAspectRatio="none"
        />

        {visibleInliers.map((correspondence, index) => {
          const pointA = correspondence.pointA;
          const pointB = correspondence.pointB;

          const lineB = epipolarLineInB(geometry.F, pointA);
          const clippedB = clipLineToImage(
            lineB.a,
            lineB.b,
            lineB.c,
            widthB,
            heightB,
          );

          const lineA = epipolarLineInA(geometry.F, pointB);
          const clippedA = clipLineToImage(
            lineA.a,
            lineA.b,
            lineA.c,
            widthA,
            heightA,
          );

          const pointAX = pointA.x * scaleAX;
          const pointAY = pointA.y * scaleAY;
          const pointBX = offsetB + pointB.x * scaleBX;
          const pointBY = pointB.y * scaleBY;

          return (
            <g key={index}>
              {clippedA && (
                <line
                  x1={clippedA[0].x * scaleAX}
                  y1={clippedA[0].y * scaleAY}
                  x2={clippedA[1].x * scaleAX}
                  y2={clippedA[1].y * scaleAY}
                  stroke="#5cfa76"
                  strokeWidth="1.5"
                  strokeOpacity="0.8"
                  vectorEffect="non-scaling-stroke"
                />
              )}

              {clippedB && (
                <line
                  x1={offsetB + clippedB[0].x * scaleBX}
                  y1={clippedB[0].y * scaleBY}
                  x2={offsetB + clippedB[1].x * scaleBX}
                  y2={clippedB[1].y * scaleBY}
                  stroke="#5cfa76"
                  strokeWidth="1.5"
                  strokeOpacity="0.8"
                  vectorEffect="non-scaling-stroke"
                />
              )}

              <circle
                cx={pointAX}
                cy={pointAY}
                r="5"
                fill="none"
                stroke="#ffffff"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
              <circle
                cx={pointBX}
                cy={pointBY}
                r="5"
                fill="none"
                stroke="#ffffff"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}
      </svg>

      <div className="reconstruction-stage-message">
        Showing {visibleInliers.length} epipolar constraints from{" "}
        {geometry.inliers.length} RANSAC inliers.
      </div>
    </div>
  );
}

function MatchVisualization({ imageA, imageB, featuresA, featuresB, matches }) {
  const correspondences = matches
    .map((match) => {
      const pointA = featuresA?.keypoints[match.indexA];
      const pointB = featuresB?.keypoints[match.indexB];

      return {
        pointA,
        pointB,
        match,
      };
    })
    .filter((correspondence) => correspondence.pointA && correspondence.pointB);

  return (
    <CorrespondenceVisualization
      imageA={imageA}
      imageB={imageB}
      featuresA={featuresA}
      featuresB={featuresB}
      correspondences={correspondences}
      message={`Showing ${Math.min(
        correspondences.length,
        250,
      )} of ${correspondences.length} descriptor matches.`}
    />
  );
}

function ReconstructedPoints({ points }) {
  const { positions, colors } = useMemo(() => {
    const positionArray = new Float32Array(points.length * 3);
    const colorArray = new Float32Array(points.length * 3);

    for (let i = 0; i < points.length; i += 1) {
      const point = points[i];
      const sourceIntensity = Number.isFinite(point.intensity)
        ? point.intensity
        : 1;
      const intensity = 0.15 + 0.85 * Math.pow(sourceIntensity, 0.7);

      positionArray[i * 3] = -point.x;
      positionArray[i * 3 + 1] = -point.y;
      positionArray[i * 3 + 2] = point.z;

      colorArray[i * 3] = intensity;
      colorArray[i * 3 + 1] = intensity;
      colorArray[i * 3 + 2] = intensity;
    }

    return {
      positions: positionArray,
      colors: colorArray,
    };
  }, [points]);

  return (
    <points>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-color" args={[colors, 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.05} sizeAttenuation vertexColors />
    </points>
  );
}

function SemiDensePoints({ points }) {
  const { positions, colors } = useMemo(() => {
    const positionArray = new Float32Array(points.length * 3);
    const colorArray = new Float32Array(points.length * 3);

    for (let i = 0; i < points.length; i += 1) {
      const point = points[i];
      const sourceIntensity = Number.isFinite(point.intensity)
        ? point.intensity
        : 1;
      const intensity = 0.15 + 0.85 * Math.pow(sourceIntensity, 0.7);

      positionArray[i * 3] = -point.x;
      positionArray[i * 3 + 1] = -point.y;
      positionArray[i * 3 + 2] = point.z;

      colorArray[i * 3] = intensity;
      colorArray[i * 3 + 1] = intensity;
      colorArray[i * 3 + 2] = intensity;
    }

    return {
      positions: positionArray,
      colors: colorArray,
    };
  }, [points]);

  if (points.length === 0) {
    return null;
  }

  return (
    <points>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-color" args={[colors, 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.04} sizeAttenuation vertexColors />
    </points>
  );
}

function CameraMarker({ position, rotation, label }) {
  return (
    <group position={position} rotation={rotation}>
      <mesh>
        <boxGeometry args={[0.12, 0.08, 0.08]} />
        <meshBasicMaterial color="#ffffff" wireframe />
      </mesh>
      <axesHelper args={[0.3]} />
    </group>
  );
}

function ReconstructionCamera({ points }) {
  const { camera, controls } = useThree();

  const bounds = useMemo(() => {
    if (!points || points.length === 0) {
      return null;
    }

    const validPoints = points.filter(
      (point) =>
        Number.isFinite(point.x) &&
        Number.isFinite(point.y) &&
        Number.isFinite(point.z),
    );

    if (validPoints.length === 0) {
      return null;
    }

    const xs = validPoints.map((point) => point.x).sort((a, b) => a - b);
    const ys = validPoints.map((point) => point.y).sort((a, b) => a - b);
    const zs = validPoints.map((point) => point.z).sort((a, b) => a - b);

    function percentile(values, p) {
      const index = Math.min(
        values.length - 1,
        Math.max(0, Math.floor((values.length - 1) * p)),
      );
      return values[index];
    }

    const minX = percentile(xs, 0.025);
    const maxX = percentile(xs, 0.975);
    const minY = percentile(ys, 0.025);
    const maxY = percentile(ys, 0.975);
    const minZ = percentile(zs, 0.025);
    const maxZ = percentile(zs, 0.975);

    const center = [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2];
    const size = Math.max(maxX - minX, maxY - minY, maxZ - minZ);

    return { center, size };
  }, [points]);

  useEffect(() => {
    if (!bounds) {
      return;
    }

    const [x, y, z] = bounds.center;
    const distance = Math.max(bounds.size * 1.15, 1);

    camera.position.set(x + distance * 0.65, y + distance * 0.35, z - distance);
    camera.near = Math.max(distance / 1000, 0.001);
    camera.far = Math.max(distance * 20, 100);
    camera.updateProjectionMatrix();
    camera.lookAt(x, y, z);

    if (controls) {
      controls.target.set(x, y, z);
      controls.update();
    }
  }, [bounds, camera, controls]);

  return null;
}

function ReconstructionScene({
  reconstruction,
  semiDensePoints,
  showSparse,
  showSemiDense,
  showCameras,
  showAxes,
}) {
  const visualizationPoints = useMemo(
    () => [...reconstruction.points, ...semiDensePoints],
    [reconstruction.points, semiDensePoints],
  );

  // Camera B position
  const cameraBPosition = useMemo(() => {
    const R = reconstruction.R;
    const t = reconstruction.t;

    return [
      -(R.get(0, 0) * t[0] + R.get(1, 0) * t[1] + R.get(2, 0) * t[2]),
      -(R.get(0, 1) * t[0] + R.get(1, 1) * t[1] + R.get(2, 1) * t[2]),
      -(R.get(0, 2) * t[0] + R.get(1, 2) * t[1] + R.get(2, 2) * t[2]),
    ];
  }, [reconstruction]);

  return (
    <>
      <ambientLight intensity={0.8} />
      <ReconstructionCamera points={visualizationPoints} />

      {showSparse && <ReconstructedPoints points={reconstruction.points} />}
      {showSemiDense && <SemiDensePoints points={semiDensePoints} />}

      {showCameras && (
        <>
          <CameraMarker position={[0, 0, 0]} rotation={[0, 0, 0]} label="A" />
          <CameraMarker
            position={cameraBPosition}
            rotation={[0, 0, 0]}
            label="B"
          />
        </>
      )}

      {showAxes && <axesHelper args={[1]} />}
      <OrbitControls makeDefault />
    </>
  );
}

function ViewerToggle({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: `1px solid ${active ? "#5cfa76" : "#555"}`,
        background: active ? "rgba(92, 250, 118, 0.12)" : "#111",
        color: active ? "#5cfa76" : "#888",
        padding: "6px 9px",
        font: "inherit",
        fontSize: "11px",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function DisparityVisualization({ disparity }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!disparity || !canvasRef.current) {
      return;
    }

    const { width, height, disparities, confidence } = disparity;
    const validDisparities = [];

    for (const value of disparities) {
      if (Number.isFinite(value)) {
        validDisparities.push(value);
      }
    }

    if (validDisparities.length === 0) {
      return;
    }

    validDisparities.sort((a, b) => a - b);

    const percentile = (p) => {
      const index = Math.min(
        validDisparities.length - 1,
        Math.max(0, Math.floor(p * (validDisparities.length - 1))),
      );
      return validDisparities[index];
    };

    const low = percentile(0.02);
    const high = percentile(0.98);
    const range = Math.max(high - low, 1e-6);
    const canvas = canvasRef.current;

    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    const imageData = context.createImageData(width, height);

    for (let i = 0; i < disparities.length; i += 1) {
      const disparityValue = disparities[i];
      const outputIndex = i * 4;

      if (!Number.isFinite(disparityValue)) {
        imageData.data[outputIndex] = 0;
        imageData.data[outputIndex + 1] = 0;
        imageData.data[outputIndex + 2] = 0;
        imageData.data[outputIndex + 3] = 255;
        continue;
      }

      const normalized = Math.max(
        0,
        Math.min(1, (disparityValue - low) / range),
      );

      const confidenceValue =
        confidence && Number.isFinite(confidence[i])
          ? Math.max(0, Math.min(1, confidence[i]))
          : 1;

      const value = Math.round(
        255 * normalized * (0.35 + 0.65 * confidenceValue),
      );

      imageData.data[outputIndex] = value;
      imageData.data[outputIndex + 1] = value;
      imageData.data[outputIndex + 2] = value;
      imageData.data[outputIndex + 3] = 255;
    }

    context.putImageData(imageData, 0, 0);
  }, [disparity]);

  if (!disparity) {
    return (
      <div className="reconstruction-stage-message">
        No dense disparity available.
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        background: "#080a09",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          imageRendering: "pixelated",
        }}
      />
    </div>
  );
}

function MatchStatusVisualization({ disparity }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!disparity?.matchStatus || !canvasRef.current) {
      return;
    }

    const { width, height, matchStatus } = disparity;
    const canvas = canvasRef.current;

    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    const imageData = context.createImageData(width, height);

    for (let i = 0; i < matchStatus.length; i += 1) {
      const status = matchStatus[i];
      const outputIndex = i * 4;

      let red = 0;
      let green = 0;
      let blue = 0;

      switch (status) {
        case 1:
          red = 40;
          green = 220;
          blue = 80;
          break;
        case 2:
          red = 230;
          green = 60;
          blue = 60;
          break;
        case 3:
          red = 235;
          green = 210;
          blue = 50;
          break;
        case 4:
          red = 50;
          green = 120;
          blue = 235;
          break;
        case 5:
          red = 180;
          green = 70;
          blue = 220;
          break;
        default:
          break;
      }

      imageData.data[outputIndex] = red;
      imageData.data[outputIndex + 1] = green;
      imageData.data[outputIndex + 2] = blue;
      imageData.data[outputIndex + 3] = 255;
    }

    context.putImageData(imageData, 0, 0);
  }, [disparity]);

  if (!disparity?.matchStatus) {
    return (
      <div className="reconstruction-stage-message">
        No dense match-status data available.
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        background: "#080a09",
        position: "relative",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          imageRendering: "pixelated",
        }}
      />

      <div
        style={{
          position: "absolute",
          right: "12px",
          bottom: "12px",
          padding: "8px 10px",
          background: "rgba(0, 0, 0, 0.8)",
          border: "1px solid #444",
          fontSize: "11px",
          lineHeight: 1.6,
        }}
      >
        <div style={{ color: "rgb(40, 220, 80)" }}>■ ACCEPTED</div>
        <div style={{ color: "rgb(230, 60, 60)" }}>■ LOW SCORE</div>
        <div style={{ color: "rgb(235, 210, 50)" }}>■ AMBIGUOUS</div>
        <div style={{ color: "rgb(50, 120, 235)" }}>■ REVERSE REJECTED</div>
        <div style={{ color: "rgb(180, 70, 220)" }}>■ LR INCONSISTENT</div>
      </div>
    </div>
  );
}

function DenseMeshCamera({ denseMesh, viewMode }) {
  const { camera, controls } = useThree();

  const bounds = useMemo(() => {
    if (!denseMesh || denseMesh.vertexCount === 0) {
      return null;
    }

    const positions = denseMesh.positions;
    const xs = [];
    const ys = [];
    const zs = [];

    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      const z = positions[i + 2];

      if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
        xs.push(x);
        ys.push(y);
        zs.push(z);
      }
    }

    if (xs.length === 0) {
      return null;
    }

    xs.sort((a, b) => a - b);
    ys.sort((a, b) => a - b);
    zs.sort((a, b) => a - b);

    function percentile(values, p) {
      const index = Math.min(
        values.length - 1,
        Math.max(0, Math.floor((values.length - 1) * p)),
      );
      return values[index];
    }

    const minX = percentile(xs, 0.02);
    const maxX = percentile(xs, 0.98);
    const minY = percentile(ys, 0.02);
    const maxY = percentile(ys, 0.98);
    const minZ = percentile(zs, 0.02);
    const maxZ = percentile(zs, 0.98);

    return {
      center: [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2],
      size: Math.max(maxX - minX, maxY - minY, maxZ - minZ),
    };
  }, [denseMesh]);

  useEffect(() => {
    if (!bounds) {
      return;
    }

    const [x, y, z] = bounds.center;
    const distance = Math.max(bounds.size * 1.15, 1);

    if (viewMode === "front") {
      camera.position.set(0, 0, 0);
      camera.up.set(0, 1, 0);
      camera.near = 0.01;
      camera.far = Math.max(distance * 20, 100);
      camera.updateProjectionMatrix();
      camera.lookAt(x, y, z);

      if (controls) {
        controls.target.set(x, y, z);
        controls.update();
      }

      return;
    }

    camera.position.set(x + distance * 0.65, y + distance * 0.35, z - distance);
    camera.up.set(0, 1, 0);
    camera.near = Math.max(distance / 1000, 0.001);
    camera.far = Math.max(distance * 20, 100);
    camera.updateProjectionMatrix();
    camera.lookAt(x, y, z);

    if (controls) {
      controls.target.set(x, y, z);
      controls.update();
    }
  }, [bounds, camera, controls, viewMode]);

  return null;
}

function DenseMeshVisualization({ denseMesh, imageA }) {
  const [viewMode, setViewMode] = React.useState("front");

  const texture = useMemo(() => {
    if (!imageA) {
      return null;
    }

    const loadedTexture = new THREE.TextureLoader().load(imageA.url);
    loadedTexture.colorSpace = THREE.SRGBColorSpace;
    loadedTexture.flipY = true;
    loadedTexture.minFilter = THREE.LinearFilter;
    loadedTexture.magFilter = THREE.LinearFilter;

    return loadedTexture;
  }, [imageA]);

  useEffect(() => {
    return () => {
      texture?.dispose();
    };
  }, [texture]);

  if (!denseMesh || denseMesh.triangleCount === 0) {
    return (
      <div className="reconstruction-stage-message">
        No dense mesh available.
      </div>
    );
  }

  return (
    <div className="reconstruction-dense-view">
      <Canvas
        camera={{
          position: [0, 0, 10],
          fov: 50,
          near: 0.01,
          far: 100000,
        }}
      >
        <DenseMeshCamera denseMesh={denseMesh} viewMode={viewMode} />

        <mesh>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              args={[denseMesh.positions, 3]}
            />
            <bufferAttribute attach="attributes-uv" args={[denseMesh.uvs, 2]} />
            <bufferAttribute attach="index" args={[denseMesh.indices, 1]} />
          </bufferGeometry>
          <meshBasicMaterial map={texture} side={THREE.DoubleSide} />
        </mesh>

        <OrbitControls makeDefault />
      </Canvas>

      <div className="reconstruction-point-cloud-controls">
        <ViewerToggle
          label="ORBIT"
          active={viewMode === "orbit"}
          onClick={() => setViewMode("orbit")}
        />
        <ViewerToggle
          label="FRONT"
          active={viewMode === "front"}
          onClick={() => setViewMode("front")}
        />
      </div>
    </div>
  );
}

function ReconstructionVisualization({
  reconstruction,
  semiDenseReconstruction,
  denseDisparity,
  denseMesh,
  imageA,
  viewerMode,
  onViewerModeChange,
}) {
  const [showSparse, setShowSparse] = React.useState(true);
  const [showSemiDense, setShowSemiDense] = React.useState(true);
  const [showCameras, setShowCameras] = React.useState(true);
  const [showAxes, setShowAxes] = React.useState(true);

  if (!reconstruction || reconstruction.points.length === 0) {
    return (
      <div className="reconstruction-stage-message">
        No valid 3D reconstruction available.
      </div>
    );
  }

  const semiDensePoints = semiDenseReconstruction?.points ?? [];

  return (
    <div className="reconstruction-3d-visualization">
      {viewerMode === "point-cloud" && (
        <Canvas
          camera={{
            position: [2, 1.5, -3],
            fov: 50,
            near: 0.01,
            far: 1000,
          }}
        >
          <ReconstructionScene
            reconstruction={reconstruction}
            semiDensePoints={semiDensePoints}
            showSparse={showSparse}
            showSemiDense={showSemiDense}
            showCameras={showCameras}
            showAxes={showAxes}
          />
        </Canvas>
      )}

      {viewerMode === "dense" && (
        <DenseMeshVisualization denseMesh={denseMesh} imageA={imageA} />
      )}

      {viewerMode === "disparity" && (
        <DisparityVisualization disparity={denseDisparity} />
      )}

      {viewerMode === "match-status" && (
        <MatchStatusVisualization disparity={denseDisparity} />
      )}

      <div className="reconstruction-view-mode-controls">
        <ViewerToggle
          label="POINT CLOUD"
          active={viewerMode === "point-cloud"}
          onClick={() => onViewerModeChange("point-cloud")}
        />
        <ViewerToggle
          label="DENSE"
          active={viewerMode === "dense"}
          onClick={() => onViewerModeChange("dense")}
        />
        <ViewerToggle
          label="DISPARITY"
          active={viewerMode === "disparity"}
          onClick={() => onViewerModeChange("disparity")}
        />
        <ViewerToggle
          label="MATCH STATUS"
          active={viewerMode === "match-status"}
          onClick={() => onViewerModeChange("match-status")}
        />
      </div>

      {viewerMode === "point-cloud" && (
        <div className="reconstruction-point-cloud-controls">
          <ViewerToggle
            label="SPARSE"
            active={showSparse}
            onClick={() => setShowSparse((value) => !value)}
          />
          <ViewerToggle
            label="SEMI-DENSE"
            active={showSemiDense}
            onClick={() => setShowSemiDense((value) => !value)}
          />
          <ViewerToggle
            label="CAMERAS"
            active={showCameras}
            onClick={() => setShowCameras((value) => !value)}
          />
          <ViewerToggle
            label="AXES"
            active={showAxes}
            onClick={() => setShowAxes((value) => !value)}
          />
        </div>
      )}

      <div className="reconstruction-stage-message">
        {viewerMode === "point-cloud" && (
          <>
            {reconstruction.points.length} sparse points ·{" "}
            {semiDensePoints.length} semi-dense points · reconstruction up to
            scale
          </>
        )}

        {viewerMode === "dense" && (
          <>
            {denseMesh?.vertexCount ?? 0} dense vertices ·{" "}
            {denseMesh?.triangleCount ?? 0} triangles
          </>
        )}

        {viewerMode === "disparity" && (
          <>
            {denseDisparity?.disparities?.reduce(
              (count, value) => count + (Number.isFinite(value) ? 1 : 0),
              0,
            ) ?? 0}{" "}
            valid dense stereo disparities
          </>
        )}
      </div>
    </div>
  );
}

export default function MatchView({
  stage,
  imageA,
  imageB,
  pendingImageA,
  pendingImageB,
  featuresA,
  featuresB,
  matches,
  geometry,
  reconstruction,
  semiDenseReconstruction,
  denseDisparity,
  denseMesh,
  onImageAChange,
  onImageBChange,
  onRunCustomReconstruction,
  reconstructionView,
  onReconstructionViewChange,
}) {
  if (stage === "matches") {
    return (
      <div className="reconstruction-match-view">
        <div className="reconstruction-stage-header">
          <span>TWO-VIEW RECONSTRUCTION</span>
          <strong>MATCHES</strong>
        </div>

        <MatchVisualization
          imageA={imageA}
          imageB={imageB}
          featuresA={featuresA}
          featuresB={featuresB}
          matches={matches}
        />
      </div>
    );
  }

  if (stage === "reconstruction") {
    return (
      <div className="reconstruction-match-view">
        <div className="reconstruction-stage-header">
          <span>TWO-VIEW RECONSTRUCTION</span>
          <strong>3D RECONSTRUCTION</strong>
        </div>

        <ReconstructionVisualization
          reconstruction={reconstruction}
          semiDenseReconstruction={semiDenseReconstruction}
          denseDisparity={denseDisparity}
          denseMesh={denseMesh}
          imageA={imageA}
          viewerMode={reconstructionView}
          onViewerModeChange={onReconstructionViewChange}
        />
      </div>
    );
  }

  if (stage === "epipolar") {
    return (
      <div className="reconstruction-match-view">
        <div className="reconstruction-stage-header">
          <span>TWO-VIEW RECONSTRUCTION</span>
          <strong>EPIPOLAR GEOMETRY</strong>
        </div>

        <EpipolarVisualization
          imageA={imageA}
          imageB={imageB}
          featuresA={featuresA}
          featuresB={featuresB}
          geometry={geometry}
        />
      </div>
    );
  }

  const showFeatures = stage === "features";
  const displayedImageA =
    stage === "input" ? (pendingImageA ?? imageA) : imageA;
  const displayedImageB =
    stage === "input" ? (pendingImageB ?? imageB) : imageB;
  const customPairReady = Boolean(pendingImageA) && Boolean(pendingImageB);

  return (
    <div className="reconstruction-match-view">
      <div className="reconstruction-stage-header">
        <span>TWO-VIEW RECONSTRUCTION</span>
        <strong>{stage.toUpperCase()}</strong>
      </div>

      <div className="reconstruction-image-pair">
        <ImageInput
          label="IMAGE A"
          image={displayedImageA}
          features={featuresA}
          showFeatures={showFeatures}
          onChange={onImageAChange}
        />
        <ImageInput
          label="IMAGE B"
          image={displayedImageB}
          features={featuresB}
          showFeatures={showFeatures}
          onChange={onImageBChange}
        />
      </div>

      <div className="reconstruction-stage-message">
        {stage === "input" && (
          <>
            <div>
              Load two views of the same scene, then run reconstruction.
            </div>
            <button
              type="button"
              className="reconstruction-button"
              onClick={onRunCustomReconstruction}
              style={{
                marginTop: "12px",
                opacity: customPairReady ? 1 : 0.45,
                cursor: customPairReady ? "pointer" : "not-allowed",
              }}
            >
              RUN RECONSTRUCTION
            </button>
          </>
        )}

        {stage === "features" && (
          <>ORB keypoints and descriptors extracted from both views.</>
        )}
      </div>
    </div>
  );
}
